import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ScrollHeading from '@/components/ScrollHeading';
import { pagesApi, researchApi } from '@/lib/supabase';
import { useLocalizedContent } from '@/hooks/useLocalizedContent';
import { Loader2 } from 'lucide-react';
import { optimizeImageUrl } from '@/lib/imageOptimize';

const Research = () => {
  const [pageData, setPageData] = useState(null);
  const [researchData, setResearchData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const { getField, getContent, isArabic } = useLocalizedContent();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [page, projects] = await Promise.all([
          pagesApi.getPublishedBySlug('research').catch(() => null),
          researchApi.getPublished()
        ]);
        setPageData(page);
        setResearchData(projects || []);
      } catch (err) {
        console.error('Error fetching research:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const sections = pageData?.sections || [];
  const introSection = sections.find(s => s.section_type === 'intro');
  const introContent = introSection ? getContent(introSection) : null;

  return (
    <div className="min-h-screen bg-[#FDFBF7] px-6 md:px-12 lg:px-24 py-24 md:py-32">
      <div>
        <ScrollHeading 
          className="heading-display mb-16"
          dataTestId="research-heading"
        >
          {(pageData && getField(pageData, 'title')) || t('research.title')}
        </ScrollHeading>

        {researchData.length === 0 ? (
          <div className="text-center py-16 max-w-6xl border-t border-[#E5E0D6]">
            <p className="body-small text-muted-foreground">
              {t('research.noProjects')}
            </p>
          </div>
        ) : (
          <div className="max-w-6xl space-y-0 border-t border-[#E5E0D6]">
            {researchData.map((project, index) => (
              <Link
                key={project.id}
                to={`/research/${project.slug}`}
                className="block group"
              >
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.1 * index, ease: [0.16, 1, 0.3, 1] }}
                  className="relative border-b border-[#E5E0D6] hover:bg-[#F5F2EB] transition-colors duration-500"
                  data-testid={`research-project-${project.id}`}
                >
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                    {/* Image Column */}
                    <div className="md:col-span-4">
                      <div className="aspect-[4/3] overflow-hidden">
                        {project.phases?.[0]?.image ? (
                          <img 
                            src={optimizeImageUrl(project.phases[0].image, 600)} 
                            alt={getField(project, 'title')}
                            loading="lazy"
                            width="400"
                            height="300"
                            className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:sepia transition-all duration-700"
                          />
                        ) : (
                          <div className="w-full h-full bg-[#E5E0D6]" />
                        )}
                      </div>
                    </div>
                    
                    {/* Content Column */}
                    <div className="md:col-span-8 py-8 pr-8 pl-8 md:pl-0">
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                        <div className="md:col-span-2">
                          <span className="caption text-[#1C1C1C]">
                            {project.year}
                          </span>
                        </div>
                        
                        <div className="md:col-span-7">
                          <h3 className="heading-medium mb-3 group-hover:text-[#8B6F47] transition-colors" data-testid={`project-title-${project.id}`}>
                            {getField(project, 'title')}
                          </h3>
                          <p className="font-sans text-base leading-relaxed text-[#1C1C1C]">
                            {getField(project, 'short_description')}
                          </p>
                        </div>
                        
                        <div className="md:col-span-3 flex items-start md:justify-end">
                          <span className="caption text-[#1C1C1C]">
                            {project.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        )}

        {introContent?.description && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="mt-16 max-w-2xl"
          >
            <p className="font-lexend font-light text-base leading-relaxed tracking-wide text-[#8C8C8C]">
              {introContent.description}
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Research;
